import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  type User,
} from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import { useRouter } from 'vue-router'
import type { User as UserProfile } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<User | null>(null)
  const userProfile = ref<UserProfile | null>(null)
  const authReady = ref(false)
  const router = useRouter()

  const isAuthenticated = computed(() => currentUser.value !== null)
  const isSuperAdmin = computed(() => userProfile.value?.is_super_admin === true)

  async function login(email: string, password: string) {
    const result = await signInWithEmailAndPassword(auth, email, password)
    await loadUserProfile(result.user.uid)
  }

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, provider)
    await loadUserProfile(result.user.uid)
  }

  async function logout() {
    await signOut(auth)
    currentUser.value = null
    userProfile.value = null
  }

  async function loadUserProfile(uid: string) {
    const snap = await getDoc(doc(db, 'users', uid))
    if (snap.exists()) {
      const profile = { user_id: uid, ...snap.data() } as UserProfile
      if (profile.is_active === false) {
        await logout()
        router.push({ name: 'login', query: { deactivated: '1' } })
        return
      }
      userProfile.value = profile
    } else {
      userProfile.value = null
    }
  }

  async function saveUserProfile(data: Partial<UserProfile>) {
    if (!currentUser.value) return
    const uid = currentUser.value.uid
    await setDoc(doc(db, 'users', uid), data, { merge: true })
    userProfile.value = { user_id: uid, ...data } as UserProfile
  }

  function initAuth() {
    onAuthStateChanged(auth, async (user) => {
      currentUser.value = user
      if (user) {
        await loadUserProfile(user.uid)
        if (router.currentRoute.value.name === 'login') {
          const redirect = router.currentRoute.value.query.redirect as string | undefined
          if (userProfile.value?.agency_id) {
            router.push(redirect || '/incidents')
          } else {
            router.push({ name: 'onboarding', query: redirect ? { redirect } : undefined })
          }
        }
      } else {
        userProfile.value = null
      }
      authReady.value = true
    })
  }

  return {
    currentUser,
    userProfile,
    authReady,
    isAuthenticated,
    isSuperAdmin,
    login,
    loginWithGoogle,
    logout,
    saveUserProfile,
    initAuth,
  }
})
